import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { P, match } from "ts-pattern";
import { useProjectContext } from "components/pages/FeaturedProjects/contexts/Project";

export enum EBanner {
	DRAFT_PROJECT = "DRAFT_PROJECT",
	PUBLISHED_PROJECT = "PUBLISHED_PROJECT",
}

export type IBannerContext = {
	openedBanner: EBanner | null;
	closeBanner: () => void;
};

type IProps = {
	children: ReactNode;
};

const BannerContext = createContext<IBannerContext>(undefined!);

export function BannerProvider(props: IProps) {
	const { openedBanner, closeBanner } = useBanner();

	return (
		<BannerContext.Provider
			value={{
				openedBanner,
				closeBanner,
			}}>
			{props.children}
		</BannerContext.Provider>
	);
}

export const useBannerContext = () => {
	const context = useContext(BannerContext);

	if (context === undefined) {
		throw new Error("useBannerContext must be used within a BannerProvider");
	}

	return context;
};

const useBanner = () => {
	const [openedBanner, setOpenedBanner] = useState<EBanner | null>(null);
	const { project } = useProjectContext();

	const closeBanner = useCallback(() => {
		setOpenedBanner(null);
	}, []);

	useEffect(() => {
		setOpenedBanner(
			match(project.publishedAt)
				.with(P.nullish, () => EBanner.DRAFT_PROJECT)
				.otherwise(() => EBanner.PUBLISHED_PROJECT),
		);
	}, [project]);

	return {
		openedBanner,
		closeBanner,
	};
};
